import { HttpClient } from '@euler/lib/request';
import { BiCardQuery } from '@euler/model/bi/blend';
import { BiChangeSetDetail } from '@euler/model/bi/change-set';
import {
  BiApplyOpsRequest,
  BiApplyOpsResult,
  BiCardData,
  BiCardDataRequest,
  BiCreateDashboardRequest,
  BiCreateDefaultDashboardsResult,
  BiDashboardEntry,
  BiDashboardFilters,
  BiDashboardGrantView,
  BiDashboardRevisionView,
  BiDashboardSummary,
  BiDashboardView,
  BiDashboardVisibility,
  BiReplaceGrantsRequest,
  BiUndoRequest,
} from '@euler/model/bi/dashboard';
import {
  BiDatasetGrantView,
  BiDatasetVisibility,
  BiUploadedDatasetView,
} from '@euler/model/bi/dataset';
import {
  BiCreateDraftRequest,
  BiDraftDetail,
  BiDraftView,
} from '@euler/model/bi/draft';
import {
  BiDashboardExportList,
  BiDashboardExportView,
  BiListDashboardExportsRequest,
  BiRequestDashboardExport,
} from '@euler/model/bi/export';
import {
  BiDatasetDetail,
  BiDatasetGrantInput,
  BiDatasetList,
  BiDatasetPreview,
  BiDatasetSharingResult,
  BiDatasetUsage,
  BiListDatasetsRequest,
  BiUpdateDatasetRequest,
} from '@euler/model/bi/library';
import { BiQueryResult } from '@euler/model/bi/result';
import {
  BiCreateFromTemplateRequest,
  BiTemplateSummary,
} from '@euler/model/bi/template';
import { BiUploadView } from '@euler/model/bi/upload';

export class BiService {
  constructor(private readonly api: HttpClient) {}
  /** A draft, with the dashboards it was added to. */
  async draft(draftId: string): Promise<BiDraftDetail> {
    return await this.api
      .get()
      .url('/bi/drafts/:draftId', { draftId })
      .future();
  }

  /** Dashboards the caller can see in the current store. */
  async dashboards(): Promise<BiDashboardSummary[]> {
    return await this.api.get().url('/bi/dashboards').future();
  }
  /** Where BI opens: the caller's default dashboard, if any. */
  async dashboardEntry(): Promise<BiDashboardEntry> {
    return await this.api.get().url('/bi/dashboards/entry').future();
  }
  /** The templates, the caller's set marked. */
  async templates(): Promise<BiTemplateSummary[]> {
    return await this.api.get().url('/bi/templates').future();
  }
  /** One dashboard from a template; 我的看板 may name a colleague. */
  async createDashboardFromTemplate(
    request: BiCreateFromTemplateRequest,
  ): Promise<BiDashboardView> {
    return await this.api
      .post()
      .url('/bi/dashboards/from-template')
      .data(request)
      .future();
  }
  /** The caller's default set; their default is the first. */
  async createDefaultDashboards(): Promise<BiCreateDefaultDashboardsResult> {
    return await this.api.post().url('/bi/dashboards/defaults').future();
  }
  /** Makes a dashboard the caller can see their default. */
  async setDefaultDashboard(dashboardId: string): Promise<BiDashboardView> {
    return await this.api
      .put()
      .url('/bi/dashboards/:dashboardId/default', { dashboardId })
      .future();
  }
  async createDashboard(
    request: BiCreateDashboardRequest,
  ): Promise<BiDashboardView> {
    return await this.api.post().url('/bi/dashboards').data(request).future();
  }
  async dashboard(dashboardId: string): Promise<BiDashboardView> {
    return await this.api
      .get()
      .url('/bi/dashboards/:dashboardId', { dashboardId })
      .future();
  }
  /** Queues a PDF export of the dashboard as viewed; jobs renders it. */
  async requestDashboardExport(
    dashboardId: string,
    request: BiRequestDashboardExport,
  ): Promise<BiDashboardExportView> {
    return await this.api
      .post()
      .url('/bi/dashboards/:dashboardId/exports', { dashboardId })
      .data(request)
      .future();
  }
  /** The member's exports, newest first. */
  async listDashboardExports(
    request: BiListDashboardExportsRequest,
  ): Promise<BiDashboardExportList> {
    return await this.api
      .get()
      .url('/bi/exports', {
        ...(request.beforeId ? { beforeId: request.beforeId } : {}),
        ...(request.limit ? { limit: String(request.limit) } : {}),
      })
      .future();
  }
  async getDashboardExport(exportId: string): Promise<BiDashboardExportView> {
    return await this.api
      .get()
      .url('/bi/exports/:exportId', { exportId })
      .future();
  }
  async deleteDashboard(dashboardId: string): Promise<void> {
    await this.api
      .delete()
      .url('/bi/dashboards/:dashboardId', { dashboardId })
      .future();
  }
  /** Every content change, rename included; refused on a stale revision. */
  async applyDashboardOps(
    dashboardId: string,
    request: BiApplyOpsRequest,
  ): Promise<BiApplyOpsResult> {
    return await this.api
      .post()
      .url('/bi/dashboards/:dashboardId/ops', { dashboardId })
      .data(request)
      .future();
  }
  /** One card's saved query, under the request's or the saved filters. */
  async cardData(
    cardId: string,
    request: BiCardDataRequest,
  ): Promise<BiCardData> {
    return await this.api
      .post()
      .url('/bi/cards/:cardId/data', { cardId })
      .data(request)
      .future();
  }

  async setDashboardVisibility(
    dashboardId: string,
    visibility: BiDashboardVisibility,
  ): Promise<BiDashboardView> {
    return await this.api
      .put()
      .url('/bi/dashboards/:dashboardId/visibility', { dashboardId })
      .data({ visibility })
      .future();
  }
  async dashboardGrants(dashboardId: string): Promise<BiDashboardGrantView[]> {
    return await this.api
      .get()
      .url('/bi/dashboards/:dashboardId/grants', { dashboardId })
      .future();
  }
  async replaceDashboardGrants(
    dashboardId: string,
    request: BiReplaceGrantsRequest,
  ): Promise<BiDashboardGrantView[]> {
    return await this.api
      .put()
      .url('/bi/dashboards/:dashboardId/grants', { dashboardId })
      .data(request)
      .future();
  }
  async dashboardRevisions(
    dashboardId: string,
  ): Promise<BiDashboardRevisionView[]> {
    return await this.api
      .get()
      .url('/bi/dashboards/:dashboardId/revisions', { dashboardId })
      .future();
  }
  /** Undoes the current revision only; undoing an undo redoes. */
  async undoDashboard(
    dashboardId: string,
    request: BiUndoRequest,
  ): Promise<BiDashboardView> {
    return await this.api
      .post()
      .url('/bi/dashboards/:dashboardId/undo', { dashboardId })
      .data(request)
      .future();
  }
  /** A change set the assistant proposed, with its diff and preview. */
  async changeSet(changeSetId: string): Promise<BiChangeSetDetail> {
    return await this.api
      .get()
      .url('/bi/change-sets/:changeSetId', { changeSetId })
      .future();
  }
  /** Runs a spec without keeping it: the card editor's live preview. */
  /**
   * A spec's result; with `filters`, as the dashboard viewed under them
   * runs it (the card editor's preview).
   */
  async query(
    spec: BiCardQuery,
    filters?: BiDashboardFilters,
  ): Promise<BiQueryResult> {
    return await this.api
      .post()
      .url('/bi/query')
      .data({ spec, filters })
      .future();
  }
  /** The manual editor's path to `add_card`. */
  async createDraft(request: BiCreateDraftRequest): Promise<BiDraftView> {
    return await this.api.post().url('/bi/drafts').data(request).future();
  }
  /** A section of the data library; the editor reads `builtin`. */
  async listDatasets(request: BiListDatasetsRequest): Promise<BiDatasetList> {
    return await this.api.get().url('/bi/datasets', request).future();
  }
  /** One dataset with its catalog, by model key or dataset id. */
  async getDataset(ref: string): Promise<BiDatasetDetail> {
    return await this.api.get().url('/bi/datasets/:ref', { ref }).future();
  }
  /** The first 50 rows of an uploaded dataset (`use`). */
  async previewDataset(datasetId: string): Promise<BiDatasetPreview> {
    return await this.api
      .get()
      .url('/bi/datasets/:datasetId/preview', { datasetId })
      .future();
  }
  async datasetUsage(datasetId: string): Promise<BiDatasetUsage> {
    return await this.api
      .get()
      .url('/bi/datasets/:datasetId/usage', { datasetId })
      .future();
  }
  /** Rename and describe (`manage`). */
  async updateDataset(
    datasetId: string,
    request: BiUpdateDatasetRequest,
  ): Promise<BiUploadedDatasetView> {
    return await this.api
      .patch()
      .url('/bi/datasets/:datasetId', { datasetId })
      .data(request)
      .future();
  }
  /**
   * A widening applies only with `confirmed`; without it the answer is the
   * audience it would reach.
   */
  async setDatasetVisibility(
    datasetId: string,
    visibility: BiDatasetVisibility,
    confirmed: boolean,
  ): Promise<BiDatasetSharingResult> {
    return await this.api
      .put()
      .url('/bi/datasets/:datasetId/visibility', { datasetId })
      .data({ visibility, confirmed })
      .future();
  }
  async datasetGrants(datasetId: string): Promise<BiDatasetGrantView[]> {
    return await this.api
      .get()
      .url('/bi/datasets/:datasetId/grants', { datasetId })
      .future();
  }
  /** Like the visibility: a widening applies only with `confirmed`. */
  async replaceDatasetGrants(
    datasetId: string,
    grants: BiDatasetGrantInput[],
    confirmed: boolean,
  ): Promise<BiDatasetSharingResult> {
    return await this.api
      .put()
      .url('/bi/datasets/:datasetId/grants', { datasetId })
      .data({ grants, confirmed })
      .future();
  }
  /** Refused while a live card uses it; the refusal names the dashboards. */
  async deleteDataset(datasetId: string): Promise<void> {
    await this.api
      .delete()
      .url('/bi/datasets/:datasetId', { datasetId })
      .future();
  }
  /** The caller's uploads still in progress or failed. */
  async uploads(): Promise<BiUploadView[]> {
    return await this.api.get().url('/bi/uploads').future();
  }
  /** A file upload, its sheets and their sheet uploads. */
  async upload(uploadId: string): Promise<BiUploadView> {
    return await this.api
      .get()
      .url('/bi/uploads/:uploadId', { uploadId })
      .future();
  }
  /**
   * Deletes the caller's file upload and its sheets that made no dataset;
   * refused (409) while a step runs on it.
   */
  async deleteUpload(uploadId: string): Promise<void> {
    await this.api.delete().url('/bi/uploads/:uploadId', { uploadId }).future();
  }
}
